import {useLingui} from '@lingui/react/macro';
import {Svg, Path} from 'react-native-svg';
import {useSelector} from 'react-redux';
import {useWindowDimensions, StyleSheet, View, Text, Pressable, ViewStyle} from 'react-native';
import {SafeAreaView} from 'extensions/viewport/SafeArea';
import {useDownloader} from 'interface/hooks/useDownloader';
import {ProgressBar} from 'interface/base/ProgressBar';
import {IconButton} from 'interface/base/IconButton';
import {FileList} from 'interface/stacks/FileList';
import {FileListItemLayout} from 'interface/stacks/FileListItem';
import {bytesize} from 'utils/common/data';
import {theme} from 'config/themes';
import {colors, vectors} from 'config/styles';

import * as transfers from 'store/slices/transfers';

export interface TransferDownloadsProps {
  offset: boolean;
}

export function TransferDownloads(props: TransferDownloadsProps) {
  const {t} = useLingui();
  const screen = useWindowDimensions();
  const minimized = useSelector(transfers.selectors.isDownloaderMinimized);
  const downloader = useDownloader();
  const isSmall = screen.width < 600;
  const isMinimal = screen.width < 800 || screen.height < 520;
  const isComplete = downloader.status.completed;
  const hasDownload = downloader.bulk.length > 0;
  const hasSkipped = downloader.status.skipped === downloader.status.count && downloader.status.count > 0;
  const downloaded = Math.max(0, Math.min(100, downloader.status.downloaded / downloader.status.size * 100));
  const textSizes = `${bytesize(downloader.status.downloaded, 1)} / ${bytesize( downloader.status.size, 1)}`;
  const textCount = `${downloader.status.count} File${downloader.status.count !== 1 ? 's' : ''}`;
  const textSkipped = `${downloader.status.skipped} file${downloader.status.skipped !== 1 ? 's' : ''} not downloaded`;
  const textFinished = `${downloader.status.finished} .zip file${downloader.status.finished !== 1 ? 's' : ''} downloaded.${isMinimal ? '' : ' It may take a moment for it to complete in your browser.'}`;
  const textDownload = minimized
    ? hasSkipped 
      ? t`Bulk download aborted due to skipped files`
      : isComplete
        ? t`Packaging download. Your download will complete soon.`
        : t`Bulk download in progress…`
    : `${Math.floor(downloaded)}%`;
  const textTitle = isComplete
    ? hasSkipped
      ? t`Bulk Download Failed`
      : t`Bulk Download Completed`
    : t`Bulk Downloading ${textCount}`;

  const classes = {
    root: [
      styles.root,
      !minimized && styles.maximized,
      minimized && styles.minimized,
      minimized && props.offset && styles.minimizedOffset,
      minimized && isSmall && styles.minimizedSmall,
    ],
    downloader: [
      isMinimal ? styles.fullscreen : styles.downloader,
    ],
  };

  return hasDownload ? (
    <Pressable
      style={classes.root}
      onPress={!minimized
        ? isComplete
          ? downloader.exit
          : downloader.minimize
        : downloader.stopProp}>
        {minimized
          ? <Pressable
              style={styles.downloaderMin}
              onPress={downloader.maximize}>
              <ProgressBar
                items={[
                  {value: downloaded, color: theme.transfers.progress.uploaded},
                  hasSkipped && {value: 100, color: colors.secondary.redOrange},
                ]}
                texts={[
                  {
                    value: textDownload,
                    style: [newStyles.text, newStyles.minimized, newStyles.left],
                  },
                  !isComplete && {
                    value: textSizes,
                    style: [newStyles.text, newStyles.nowrap, newStyles.minimized, newStyles.right],
                  },
                ]}
              />
            </Pressable>
          : <Pressable
              // tabIndex={-1}
              // restrictFocusWithin
              // disableTouchOpacityAnimation
              // accessibilityTraits={Types.AccessibilityTrait.Dialog}
              style={classes.downloader}
              onPress={downloader.stopProp}>
              <SafeAreaView style={{flex: 1, backgroundColor: colors.neutral.black}}>
                <View style={styles.downloadMaximized}>
                  <View style={styles.header}>
                    <Text style={styles.title}>
                      {textTitle}
                    </Text>
                    {!isComplete
                      ? <Pressable onPress={downloader.minimize}>
                          <View style={styles.close}>
                            <Svg viewBox="0 0 24 24" width={24} height={24}>
                              <Path d={vectors.icons.min} fill={colors.neutral.mid}/>
                            </Svg>
                          </View>
                        </Pressable>
                      : <Pressable onPress={downloader.exit}>
                          <View style={styles.close}>
                            <Svg viewBox="0 0 14 14" width={14} height={14}>
                              <Path d={vectors.icons.close} fill={colors.neutral.mid}/>
                            </Svg>
                          </View>
                        </Pressable>
                    }
                  </View>
                  <FileList
                    view={downloader.view}
                    items={downloader.items}
                    layout={FileListItemLayout.ListNormal}
                    options={{
                      hideHeader: true,
                      hideGutter: true,
                      disableOpen: true,
                      disableDrag: true,
                      disableFocus: true,
                      disableSelect: true,
                      disablePullToRefresh: true,
                    }}
                  />
                  <View style={styles.actions}>
                    {isComplete &&
                      <View style={[styles.buttonDownload, {cursor: 'default'} as ViewStyle]}>
                        <Svg viewBox="0 0 20 20" width={20} height={20}>
                          <Path
                            d={downloader.status.skipped === 0 ? vectors.icons.check : vectors.icons.warning}
                            fill={downloader.status.skipped === 0 ? colors.secondary.green : colors.secondary.redOrange}
                          />
                        </Svg>
                        <Text style={styles.textCompleted}>
                          {downloader.status.skipped === 0 ? textFinished : textSkipped}
                        </Text>
                      </View>
                    }
                    {(hasSkipped || isComplete) &&
                      <View style={styles.spacer}/>
                    }
                    {!isComplete && !hasSkipped &&
                      <View style={styles.progress}>
                        <ProgressBar
                          items={[
                            {value: downloaded, color: theme.transfers.progress.uploaded},
                            hasSkipped && {value: 100, color: colors.secondary.redOrange},
                          ]}
                          texts={[
                            {value: textSizes, style: [styles.progressText, styles.left]},
                            {value: textDownload, style: [styles.progressText, styles.right]},
                          ]}
                        />
                      </View>
                    }
                    <View style={styles.buttonsActive}>
                      <IconButton
                        mode="cancel"
                        onPress={downloader.exit}
                        title={t`Close bulk downloader`}
                        text={isComplete
                          ? t`Close`.toUpperCase()
                          : t`Cancel`.toUpperCase()
                        }
                      />
                    </View>
                  </View>
                </View>
              </SafeAreaView>
            </Pressable>
        }
      </Pressable>
  ) : null;
}

export const newStyles = StyleSheet.create({
  text: {
    flex: 0,
    fontSize: 12,
  },
  main: {
    color: theme.transfers.headerText,
  },
  minimized: {
    color: colors.neutral.light,
  },
  left: {
    flex: 1,
  },
  right: {
    flex: 0,
  },
  nowrap: {
    // @ts-ignore
    whiteSpace: 'nowrap',
  },
});

export const styles = StyleSheet.create({
  root: {
    position: 'absolute',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.dialog.overlay,
    // @ts-ignore
    cursor: 'default',
  },
  maximized: {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  minimized: {
    width: 350,
    height: 62,
    alignItems: 'center',
    justifyContent: 'center',
    borderTopWidth: 1,
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
    backgroundColor: colors.neutral.darker,
    borderColor: colors.neutral.dark,
    shadowOffset: {width: -1, height: -1},
    shadowColor: 'rgba(0,0,0,0.3)',
    shadowRadius: 3,
    elevation: 1,
    bottom: 0,
    right: 16,
    // @ts-ignore
    left: 'initial',
  },
  minimizedOffset: {
    bottom: 72,
    borderRadius: 3,
  },
  minimizedSmall: {
    left: 0,
    right: 0,
    width: undefined,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  downloader: {
    width: 780,
    height: 500,
    borderRadius: 3,
    backgroundColor: theme.transfers.fill,
    shadowOffset: {width: 0, height: 3},
    shadowColor: 'rgba(0,0,0,0.3)',
    shadowRadius: 3,
    elevation: 1,
    // @ts-ignore
    cursor: 'default',
  },
  progress: {
    marginBottom: 10,
    marginHorizontal: 18,
    justifyContent: 'center',
    flex: 1,
  },
  fullscreen: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.transfers.fill,
  },
  downloadMaximized: {
    flex: 1,
    backgroundColor: theme.transfers.fill,
  },
  spacer: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 54,
    borderBottomWidth: 1,
    borderColor: theme.transfers.headerBorder,
    backgroundColor: theme.transfers.header,
  },
  title: {
    color: theme.transfers.headerText,
    paddingLeft: 20,
    fontSize: 16,
    flex: 1,
  },
  textCompleted: {
    color: theme.transfers.headerText,
    marginLeft: 8,
    fontSize: 14,
  },
  actions: {
    flexDirection: 'row',
    height: 62,
    paddingRight: 16,
    borderTopWidth: 1,
    borderColor: theme.transfers.headerBorder,
    backgroundColor: theme.transfers.header,
  },
  close: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 54,
    height: 54,
    top: 1,
  },
  buttonsActive: {
    marginVertical: 8,
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  buttonDownload: {
    marginRight: 6,
    marginLeft: 16,
    height: 60,
    flexDirection: 'row',
    alignItems: 'center',
  },
  downloaderMin: {
    flex: 1,
    // cursor: 'pointer',
    flexDirection: 'column',
    paddingHorizontal: 18,
  },
  progressText: {
    fontSize: 12,
    color: theme.transfers.headerText,
  },
  left: {
    flex: 1,
  },
  right: {

  },
});
